import { Refund, RefundStatus } from "@/types";
import { tooltipStyles } from "@/utils/tooltipStyles";
import {
  getCancellationReasonLabel,
  getNetworkDisplayName,
} from "@/utils/utils";
import { Dialog, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useState } from "react";
import RejectConfirmationDialog from "./RejectConfirmationDialog";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import toast from "react-hot-toast";
import { truncateAddress } from "@/utils/truncateText";

interface ReviewRefundRequestModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onReject: () => void;
  selectRefund: Refund;
}

const ReviewRefundRequestModal = ({
  open,
  onClose,
  onConfirm,
  onReject,
  selectRefund,
}: ReviewRefundRequestModalProps) => {
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [showMainModal, setShowMainModal] = useState(true);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const {
    bookingCode,
    cancellationReason,
    orderContactName,
    totalRefundable,
    refundNetwork,
    walletAddress,
    bankName,
    bankAccountName,
    bankAccountNumber,
    status
  } = selectRefund;
  const handleReject = () => {
    setShowRejectConfirmation(true);
    setShowMainModal(false);
  };

  const handleRejectConfirm = () => {
    onReject();
    setShowRejectConfirmation(false);
    setShowMainModal(false);
    onClose();
  };

  const handleRejectCancel = () => {
    setShowRejectConfirmation(false);
    setShowMainModal(true);
  };

  const handleClose = () => {
    setShowRejectConfirmation(false);
    setShowMainModal(false);
    onClose();
  };

  const [, copy] = useCopyToClipboard();

  const handleCopy = (text: string) => () => {
    copy(text)
      .then(() => {
        toast.success("Copied!");
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  return (
    <>
      <RejectConfirmationDialog
        open={showRejectConfirmation}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
      />
      <Dialog
        open={open && showMainModal}
        onClose={handleClose}
        maxWidth={"md"}
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: "24px",
            width: `${isMobile ? "100%" : "670px"}`,
            margin: "16px",
          },
        }}
      >
        <div className="w-full rounded-[24px] px-8 py-6 max-sm:px-5">
          <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-4">
            <h2 className="text-xl font-medium text-[#1E293B]">
              Review Refund Request
            </h2>
            <button onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#334155"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="mt-8">
            <p className="text-[16px] text-text max-sm:text-[14px]">
              Please review the following refund details before proceeding with
              approval or rejection.
            </p>
            <div className="mt-8 space-y-[10px]">
              <div className="flex">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Booking ID
                </span>
                <span className="text-sm font-semibold text-[#1E293B]">
                  {bookingCode}
                </span>
              </div>
              <div className="flex">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Reason of cancellation
                </span>
                <span className="text-sm font-semibold text-[#1E293B] max-sm:flex-1">
                  {getCancellationReasonLabel(cancellationReason)}
                </span>
              </div>
              <div className="flex">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Contact Person
                </span>
                <span className="text-sm font-semibold text-[#1E293B]">
                  {orderContactName}
                </span>
              </div>
              <div className="flex ">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Refund Amount
                </span>
                <span className="text-sm font-semibold text-[#1E293B]">
                  {walletAddress ? "USDT" : "USD"} {totalRefundable}
                </span>
              </div>
              {walletAddress ? (
                <>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Network
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {(() => {
                        if (refundNetwork && refundNetwork?.length > 15) {
                          return (
                            <Tooltip
                              title={refundNetwork}
                              placement="right"
                              arrow
                              componentsProps={{
                                tooltip: { sx: tooltipStyles.tooltip },
                                arrow: { sx: tooltipStyles.arrow },
                              }}
                            >
                              <div className="flex flex-col">
                                {!refundNetwork ? (
                                  "-"
                                ) : (
                                  <span className="text-[14px] font-semibold text-text ">
                                    {getNetworkDisplayName(refundNetwork)}
                                  </span>
                                )}
                              </div>
                            </Tooltip>
                          );
                        }
                        return (
                          <div className="flex flex-col">
                            {!refundNetwork ? (
                              "-"
                            ) : (
                              <span className="text-[14px] font-semibold text-text">
                                {getNetworkDisplayName(refundNetwork)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Refund Address
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {isMobile ? (
                        <>
                          {(() => {
                            if (walletAddress && walletAddress?.length > 10) {
                              return (
                                <Tooltip
                                  title={walletAddress}
                                  placement="right"
                                  arrow
                                  componentsProps={{
                                    tooltip: { sx: tooltipStyles.tooltip },
                                    arrow: { sx: tooltipStyles.arrow },
                                  }}
                                >
                                  <div className="flex flex-col">
                                    {!walletAddress ? (
                                      "-"
                                    ) : (
                                      <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                        {truncateAddress(walletAddress, 4, 5)}{" "}
                                        <img
                                          src={"/images/copy.png"}
                                          alt="copy"
                                          className="cursor-pointer"
                                          onClick={handleCopy(walletAddress)}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </Tooltip>
                              );
                            }
                            return (
                              <div className="flex flex-col">
                                {!walletAddress ? (
                                  "-"
                                ) : (
                                  <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                    {walletAddress}{" "}
                                    <img
                                      src={"/images/copy.png"}
                                      alt="copy"
                                      className="cursor-pointer"
                                      onClick={handleCopy(walletAddress)}
                                    />
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      ) : (
                        <>
                          {(() => {
                            if (walletAddress && walletAddress?.length > 44) {
                              return (
                                <Tooltip
                                  title={walletAddress}
                                  placement="right"
                                  arrow
                                  componentsProps={{
                                    tooltip: { sx: tooltipStyles.tooltip },
                                    arrow: { sx: tooltipStyles.arrow },
                                  }}
                                >
                                  <div className="flex flex-col">
                                    {!walletAddress ? (
                                      "-"
                                    ) : (
                                      <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                        {truncateAddress(walletAddress)}{" "}
                                        <img
                                          src={"/images/copy.png"}
                                          alt="copy"
                                          className="cursor-pointer"
                                          onClick={handleCopy(walletAddress)}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </Tooltip>
                              );
                            }
                            return (
                              <div className="flex flex-col">
                                {!walletAddress ? (
                                  "-"
                                ) : (
                                  <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                    {walletAddress}{" "}
                                    <img
                                      src={"/images/copy.png"}
                                      alt="copy"
                                      className="cursor-pointer"
                                      onClick={handleCopy(walletAddress)}
                                    />
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Bank Name
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {bankName}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Bank Account No.
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {bankAccountNumber}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Name of Account
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {bankAccountName}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          {status !== RefundStatus.REJECTED && (
            <div className="mt-8 flex w-full items-center gap-4 max-sm:h-full max-sm:flex-col max-sm:gap-2">
              <button
                onClick={handleReject}
                className="h-10 w-1/2 rounded-[6px]  bg-[#F75C5C] text-base font-medium text-white max-sm:block max-sm:h-12 max-sm:w-full max-sm:rounded-lg"
              >
                Reject
              </button>
              <button
                onClick={onConfirm}
                className="h-10 w-1/2 rounded-[6px] bg-text text-base font-medium text-white max-sm:block max-sm:h-12 max-sm:w-full max-sm:rounded-lg"
              >
                Approve
              </button>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default ReviewRefundRequestModal;
